<script lang="ts">
  import { uiStore } from "../../lib/stores";
  import { fly, fade } from "svelte/transition";

  let notifications = $derived($uiStore.notifications);

  function removeNotification(id: string) {
    uiStore.update((state) => ({
      ...state,
      notifications: state.notifications.filter((n) => n.id !== id),
    }));
  }

  const iconPaths: Record<string, string> = {
    success: "M5 13l4 4L19 7",
    error: "M6 18L18 6M6 6l12 12",
    info: "M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
  };
</script>

<div
  class="fixed top-0 left-0 right-0 z-[9999] flex flex-col items-center pt-4 px-4 pointer-events-none"
  aria-live="polite"
>
  {#each notifications as notification (notification.id)}
    <div
      in:fly={{ y: -60, duration: 350, delay: 50 }}
      out:fly={{ y: -40, duration: 250 }}
      class="toast toast--{notification.type} pointer-events-auto mb-2 w-full max-w-md"
      role="alert"
    >
      <div class="toast__icon-wrapper toast__icon-wrapper--{notification.type}">
        <svg
          class="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2.5"
            d={iconPaths[notification.type]}
          />
        </svg>
      </div>

      <p class="toast__message">
        {notification.message}
      </p>

      <button
        onclick={() => removeNotification(notification.id)}
        class="toast__close"
        aria-label="알림 닫기"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      <!-- Auto-dismiss progress bar -->
      <div class="toast__progress toast__progress--{notification.type}"></div>
    </div>
  {/each}
</div>

<style>
  .toast {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 16px;
    border-radius: 14px;
    border: 1px solid;
    position: relative;
    overflow: hidden;
    backdrop-filter: blur(20px) saturate(180%);
    -webkit-backdrop-filter: blur(20px) saturate(180%);
    box-shadow:
      0 4px 24px rgba(0, 0, 0, 0.08),
      0 1px 4px rgba(0, 0, 0, 0.04);
  }

  .toast--success {
    background: rgba(240, 253, 244, 0.92);
    border-color: rgba(34, 197, 94, 0.2);
  }
  .toast--error {
    background: rgba(254, 242, 242, 0.92);
    border-color: rgba(239, 68, 68, 0.2);
  }
  .toast--info {
    background: rgba(239, 246, 255, 0.92);
    border-color: rgba(59, 130, 246, 0.2);
  }

  .toast__icon-wrapper {
    flex-shrink: 0;
    width: 28px;
    height: 28px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .toast__icon-wrapper--success {
    background: rgba(34, 197, 94, 0.15);
    color: #16a34a;
  }
  .toast__icon-wrapper--error {
    background: rgba(239, 68, 68, 0.15);
    color: #dc2626;
  }
  .toast__icon-wrapper--info {
    background: rgba(59, 130, 246, 0.15);
    color: #2563eb;
  }

  .toast__message {
    flex: 1;
    font-size: 14px;
    font-weight: 500;
    line-height: 1.4;
    color: var(--text-primary, #0f172a);
  }

  .toast__close {
    flex-shrink: 0;
    padding: 4px;
    border-radius: 6px;
    color: var(--text-muted, #94a3b8);
    transition: all 150ms;
    cursor: pointer;
  }
  .toast__close:hover {
    background: rgba(0, 0, 0, 0.06);
    color: var(--text-secondary, #475569);
  }

  .toast__progress {
    position: absolute;
    bottom: 0;
    left: 0;
    height: 3px;
    border-radius: 0 0 14px 14px;
    animation: toast-progress 5s linear forwards;
  }
  .toast__progress--success { background: rgba(34, 197, 94, 0.4); }
  .toast__progress--error { background: rgba(239, 68, 68, 0.4); }
  .toast__progress--info { background: rgba(59, 130, 246, 0.4); }

  @keyframes toast-progress {
    from { width: 100%; }
    to { width: 0%; }
  }
</style>
